import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MonthTarget } from "@/types/user/target";

import { DateTime } from "luxon";
import { useEffect } from "react";
import { useFetcher } from "react-router-dom";
import { MarkdownRenderer } from "../ui";
import { ActionWrapper } from "@/utils/wretch";
import { Loader2 } from "lucide-react";

interface TargetExplainProps {
    monthTarget: MonthTarget;
    component_class: string;
    at_date: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function TargetExplainDialog({ monthTarget, component_class, at_date, open, onOpenChange }: TargetExplainProps) {
    const fetcher = useFetcher<ActionWrapper<{ explain: string }>>();

    useEffect(() => {
        if (monthTarget && component_class && at_date) {
            fetcher.load(`/objectif/mensuel/${monthTarget.id}/explain/${component_class}/${at_date}`);
        }
    }, [monthTarget, component_class, at_date]);

    const component = monthTarget.components.find(_component => _component.component_class?.name === component_class);

    // Pour les composants `time_range = "monthly"` (ex. LMS), `at_date` est
    // toujours le 1er du mois et n'a pas de signification "instantanée" :
    // afficher "1 avr. 2026" induit en erreur (le détail couvre la fenêtre
    // métier complète, pas seulement ce jour-là). On rend le mois en toutes
    // lettres dans ce cas. Pour les composants daily, la date pleine reste
    // pertinente (le détail correspond à ce jour précis).
    const isMonthly = component?.component_class?.time_range === "monthly";
    const dateLabel = isMonthly ? DateTime.fromISO(at_date).setLocale("fr").toFormat("LLLL yyyy") : DateTime.fromISO(at_date).toLocaleString(DateTime.DATE_MED);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-5xl">
                <DialogHeader>
                    <DialogTitle className="flex items-baseline gap-2">
                        <span>{component?.component_class?.verbose_name}</span>
                        <span className="text-sm font-normal capitalize text-muted-foreground">{dateLabel}</span>
                    </DialogTitle>
                </DialogHeader>
                {fetcher.state == "loading" ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    </div>
                ) : (
                    <div className="max-h-[75vh] overflow-y-auto pr-2">{fetcher.data?.success && <MarkdownRenderer content={fetcher.data.content.explain} softBreaks={true} />}</div>
                )}
            </DialogContent>
        </Dialog>
    );
}
